import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { PayloadProps, UseCertificateRequestQueryParams } from "../routes/Profile/AddSkillBook/AddSkillBook.types";
import { fetcherFactory } from "../services";
import { useAuthStore } from "../stores";
import { getEnvironmentVariables } from "../utils/general";

const { basePath } = getEnvironmentVariables();

interface UseOptionsQueryParams {
  enabled?: boolean;
  queryKey?: string[];
  onSuccess?: (data: { output: [] }) => void;
  onError?: (error: string) => void;
  onSettled?: (data: { output: [] }, error: string) => void;
  knowledgeAreaId?: string | number;
}

type UseDeleteOptions = {
  onSuccess?: (data: unknown) => void;
  onError?: (error: unknown) => void;
};

export function useKnowledgeAreasOptionsQuery({
  enabled = true,
  onSuccess,
  onError
}: UseOptionsQueryParams) {

  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const isLoading = useAuthStore(state => state.isLoading);
  const corporateInfo = useAuthStore(state => state.corporateInfo);
  const fetcher = fetcherFactory();

  const uri = `/skills/${corporateInfo!.id}/knowledge-areas`;

  const query = useQuery({
    queryKey: [uri],

    queryFn: () => fetcher(
      `${basePath}${uri}`,
      {
        headers: {
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then((res) => {
      return res.json();
    }),

    enabled: Boolean(enabled && sessionToken && idToken && !isLoading) && !isSeedingSession
  });

  useEffect(() => {
    if (query.data !== undefined && onSuccess) onSuccess(query.data as { output: [] });
  }, [query.data]);

  useEffect(() => {
    if (query.error && onError) onError(query.error as unknown as string);
  }, [query.error]);

  return query;
}

export function useKnowledgeDomainsOptionsQuery({
  knowledgeAreaId,
  enabled = true,
  onSuccess,
  onError
}: UseOptionsQueryParams) {

  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const isLoading = useAuthStore(state => state.isLoading);
  const corporateInfo = useAuthStore(state => state.corporateInfo);
  const fetcher = fetcherFactory();

  const uri = `/skills/${corporateInfo!.id}/knowledge-areas/${knowledgeAreaId}/domains`;

  const query = useQuery({
    queryKey: [uri],

    queryFn: () => fetcher(
      `${basePath}${uri}`,
      {
        headers: {
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then((res) => {
      return res.json();
    })
      .catch((error) => {
        onError;
        throw error;
      }),

    enabled: Boolean(enabled && sessionToken && idToken && !isLoading) && !isSeedingSession && Boolean(knowledgeAreaId)
  });

  useEffect(() => {
    if (query.data !== undefined && onSuccess) onSuccess(query.data as { output: [] });
  }, [query.data]);

  useEffect(() => {
    if (query.error && onError) onError(query.error as unknown as string);
  }, [query.error]);

  return query;
}

export function useCapabilitiesOptionsQuery({
  enabled = true,
  onSuccess,
  onError
}: UseOptionsQueryParams) {

  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const corporateInfo = useAuthStore(state => state.corporateInfo);
  const fetcher = fetcherFactory();

  const uri = `/skills/${corporateInfo!.id}/capabilities`;

  const query = useQuery({
    queryKey: [uri],

    queryFn: () => fetcher(
      `${basePath}${uri}`,
      {
        headers: {
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then((res) => {
      return res.json();
    })
      .catch((error) => {
        onError;
        throw error;
      }),

    enabled: Boolean(enabled && sessionToken && idToken) && !isSeedingSession
  });

  useEffect(() => {
    if (query.data !== undefined && onSuccess) onSuccess(query.data as { output: [] });
  }, [query.data]);

  useEffect(() => {
    if (query.error && onError) onError(query.error as unknown as string);
  }, [query.error]);

  return query;
}

export function useCourseTypesOptionsQuery({
  enabled = true,
  onSuccess,
  onError
}: UseOptionsQueryParams) {

  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const fetcher = fetcherFactory();

  const uri = "/certificates/external/course-type";

  const query = useQuery({
    queryKey: [uri],

    queryFn: () => fetcher(
      `${basePath}${uri}`,
      {
        headers: {
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      }
    ).then((res) => {
      return res.json();
    })
      .catch((error) => {
        console.log("error", error);
        onError;
        throw error;
      }),

    enabled: Boolean(enabled && sessionToken && idToken) && !isSeedingSession
  });

  useEffect(() => {
    if (query.data !== undefined && onSuccess) onSuccess(query.data as { output: [] });
  }, [query.data]);

  useEffect(() => {
    if (query.error && onError) onError(query.error as unknown as string);
  }, [query.error]);

  return query;
}

export function useCampaignsOptionsQuery({
  enabled = true,
  onSuccess,
  onError
}: UseOptionsQueryParams) {

  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const fetcher = fetcherFactory();

  const corporateInfo = useAuthStore(state => state.corporateInfo)!;


  // L’endpoint per le campagne
  const uri = `/usermgmt/${corporateInfo.id}/collect-campaigns`;

  const query = useQuery({
    queryKey: [uri],

    queryFn: () =>
      fetcher(`${basePath}${uri}`, {
        headers: {
          authorization: idToken,
          "x-ada-session-token": sessionToken
        }
      })
        .then(res => {
          if (!res.ok) throw new Error("Errore fetching campaigns");
          return res.json() as Promise<{ output: Array<{ id: number; name: string }> }>;
        }),

    enabled: Boolean(enabled && sessionToken && idToken) && !isSeedingSession
  });

  useEffect(() => {
    if (query.data !== undefined && onSuccess) onSuccess(query.data as unknown as { output: [] });
  }, [query.data]);

  useEffect(() => {
    if (query.error && onError) onError(query.error as unknown as string);
  }, [query.error]);

  return query;
}

export function usePostCertificate({
  onSuccess,
  onError
}: UseOptionsQueryParams) {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();
  const uri = "/certificates/external/certificate-request/upload";

  return useMutation({
    mutationFn: async (data: PayloadProps) => {
      console.log("Invio dati a:", `${ basePath }${ uri }`);
      console.log("Dati inviati:", data);

      const formData = new FormData();
      formData.append(
        "certificate",
        data.certificate ? data.certificate : ""
      );
      (data.attachments ?? []).forEach((file: string) => formData.append("attachments", file));
      formData.append("data", JSON.stringify(data.data));

      console.log("formData", formData);
      try {
        const response = await fetcher(
          `${ basePath }${ uri }`,
          {
            method: "POST",
            headers: {
              "authorization": idToken,
              "x-ada-session-token": sessionToken
            },
            body: formData
          }
        );
        return response;
      } catch (error) {
        console.error("Errore durante l'invio:", error);
        throw error;
      }
    },

    onSuccess: (data) => {
      console.log("Successo dalla mutazione:", data);
      if (onSuccess) onSuccess(data as unknown as  { output: [] });
    },

    onError: (error) => {
      console.error("Errore dalla mutazione:", error);
      if (onError) onError(error as unknown as string);
    }
  });
}

export function useGetExternalCertificateRequests({
  enabled = true,
  onSuccess,
  onError,
  queryParams,
  queryKey
}: UseCertificateRequestQueryParams) {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();


  const queryString = new URLSearchParams(
    Object.entries(queryParams).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  const uri = `/certificates/external/certificate-requests/detail?${queryString}`;

  const query = useQuery({
    queryKey: [
      "external-certificate-requests", "pending", ...queryKey
    ],

    queryFn: () => fetcher(`${basePath}${uri}`, {
      headers: {
        "authorization": idToken,
        "x-ada-session-token": sessionToken
      }
    }).then(res => res.json()),

    enabled: Boolean(enabled && sessionToken && idToken)
  });

  useEffect(() => {
    if (query.data !== undefined && onSuccess) onSuccess(query.data as Parameters<NonNullable<typeof onSuccess>>[0]);
  }, [query.data]);

  useEffect(() => {
    if (query.error && onError) onError(query.error as unknown as string);
  }, [query.error]);

  return query;
}

export function useGetExternalCertificateRequestsApproved({
  enabled = true,
  onSuccess,
  onError,
  queryParams,
  queryKey
}: UseCertificateRequestQueryParams) {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();


  const queryString = new URLSearchParams(
    Object.entries(queryParams).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  const uri = `/certificates/external/certificate-requests/detail?${queryString}`;

  const query = useQuery({
    queryKey: [
      "external-certificate-requests", "APPROVED", ...queryKey
    ],

    queryFn: () => fetcher(`${basePath}${uri}`, {
      headers: {
        "authorization": idToken,
        "x-ada-session-token": sessionToken
      }
    }).then(res => res.json()),

    enabled: Boolean(enabled && sessionToken && idToken)
  });

  useEffect(() => {
    if (query.data !== undefined && onSuccess) onSuccess(query.data as Parameters<NonNullable<typeof onSuccess>>[0]);
  }, [query.data]);

  useEffect(() => {
    if (query.error && onError) onError(query.error as unknown as string);
  }, [query.error]);

  return query;
}

export function useGetExternalCertificateRequestsRejected({
  enabled = true,
  onSuccess,
  onError,
  queryParams,
  queryKey
}: UseCertificateRequestQueryParams) {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();


  const queryString = new URLSearchParams(
    Object.entries(queryParams).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  const uri = `/certificates/external/certificate-requests/detail?${queryString}`;

  const query = useQuery({
    queryKey: [
      "external-certificate-requests", "REJECTED", ...queryKey
    ],

    queryFn: () => fetcher(`${basePath}${uri}`, {
      headers: {
        "authorization": idToken,
        "x-ada-session-token": sessionToken
      }
    }).then(res => res.json()),

    enabled: Boolean(enabled && sessionToken && idToken)
  });

  useEffect(() => {
    if (query.data !== undefined && onSuccess) onSuccess(query.data as Parameters<NonNullable<typeof onSuccess>>[0]);
  }, [query.data]);

  useEffect(() => {
    if (query.error && onError) onError(query.error as unknown as string);
  }, [query.error]);

  return query;
}


export function usePostExternalTrainingConsuntivazione ({
  onSuccess,
  onError
}: UseOptionsQueryParams) {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();
  const uri = "/certificates/external/certificate-request/upload";

  return useMutation({
    mutationFn: async (data: PayloadProps) => {
      console.log("dati inviati:",data);
      const {
        certificate,
        attachments,
        data: originalData
      } = data;

      const teacher = originalData.teacher;

      const payloadData = {
        ...originalData,
        instructor: teacher,
        typeId: originalData.trainingType === ""?
          null :
          Number(originalData.trainingType)
      };

      console.log("payload dati inviati:",payloadData);

      const formData = new FormData();
      formData.append("certificate", certificate || "");
      (attachments ?? []).forEach((file: string) => formData.append("attachments", file));
      formData.append("data", JSON.stringify(payloadData));

      console.log("Invio dati a:", `${basePath}${uri}`);
      console.log("Dati inviati:", payloadData);

      try {
        const response = await fetcher(
          `${basePath}${uri}`,
          {
            method: "POST",
            headers: {
              authorization: idToken,
              "x-ada-session-token": sessionToken
            },
            body: formData
          }
        );
        return response;
      } catch (error) {
        console.error("Errore durante l'invio:", error);
        throw error;
      }
    },

    onSuccess: (data) => {
      console.log("Successo dalla mutazione:", data);
      if (onSuccess) onSuccess(data as unknown as  { output: [] });
    },

    onError: (error) => {
      console.error("Errore dalla mutazione:", error);
      if (onError) onError(error as unknown as string);
    }
  });
}

export function useDeleteExternalTraining ({ onSuccess, onError }: UseDeleteOptions) {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();

  return useMutation({
    mutationFn: async (id: number) => {
      const url = `${ basePath }/external-training/${ id }`;
      console.log("Invio DELETE a:", url);

      const response = await fetcher(url, {
        method: "DELETE",
        headers: {
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      });

      return response;
    },

    onSuccess: (data) => {
      console.log("Cancellazione riuscita:", data);
      if (onSuccess) onSuccess(data);
    },

    onError: (error) => {
      console.error("Errore durante la cancellazione:", error);
      if (onError) onError(error);
    }
  });
}

export function useDeleteExternalCertificate ({ onSuccess, onError }: UseDeleteOptions) {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();

  return useMutation({
    mutationFn: async (id: number) => {
      const url = `${ basePath }/certificates/external/${ id }`;
      console.log("Invio DELETE a:", url);

      const response = await fetcher(url, {
        method: "DELETE",
        headers: {
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      });

      return response;
    },

    onSuccess: (data) => {
      console.log("Cancellazione riuscita:", data);
      if (onSuccess) onSuccess(data);
    },

    onError: (error) => {
      console.error("Errore durante la cancellazione:", error);
      if (onError) onError(error);
    }
  });
}

export function useDownloadAttachmentsZip() {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();

  return useMutation({
    mutationFn: ({ id, isExternalCertificate }:{id: number, isExternalCertificate: boolean}) => fetcher(
      isExternalCertificate ?
        `${basePath}/certificates/external/${id}/download-attachments`
        :
        `${basePath}/external-training/${id}/download-attachments`,
      {
        headers:{
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        },
        method: "GET"
      }).then(async (res) => {
      return res.blob();
    })
      .catch(async (error) => {
        const errorRes = await error;
        throw errorRes;
      })
  });
}
